import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Input, Radio, Row, Space, Spin, Typography } from 'antd';
import { useSearchParams } from 'react-router-dom';
import MailingService, { MailingSurveyPublicQuestion, MailingSurveyPublicSurvey } from '../../services/MailingService';
import kontrolxLogo from '../../assets/auth/kontrolxlogo.svg';

const { Title, Text } = Typography;
const { TextArea } = Input;

type QuestionAnswerState = {
  selectedOptionId?: string;
  answerText?: string;
  otherText?: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
};

const MailingSurveyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [survey, setSurvey] = useState<MailingSurveyPublicSurvey | null>(null);
  const [answers, setAnswers] = useState<Record<string, QuestionAnswerState>>({});

  const token = useMemo(() => {
    const value = searchParams.get('token');
    return value?.trim() || '';
  }, [searchParams]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setSuccess(false);
      setMessage('Geçersiz anket bağlantısı.');
      return;
    }

    setLoading(true);
    MailingService.getPublicSurveyByToken(token)
      .then((response) => {
        setSurvey(response.data);
        setMessage('');
      })
      .catch((error) => {
        setSuccess(false);
        setMessage(error?.response?.data?.message || 'Anket yüklenemedi.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const setQuestionAnswer = (questionId: string, next: Partial<QuestionAnswerState>) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        ...next,
      },
    }));
  };

  const validateQuestion = (question: MailingSurveyPublicQuestion) => {
    const current = answers[question.id] || {};
    const required = question.required !== false;

    if (question.questionType === 'TEXT') {
      const text = (current.answerText || '').trim();
      if (required && !text) {
        return 'Zorunlu text soru yanıtsız bırakılamaz';
      }
      return null;
    }

    if (required && !current.selectedOptionId) {
      return 'Zorunlu çoktan seçmeli soru yanıtsız bırakılamaz';
    }

    const selectedOption = question.options?.find((option) => option.id === current.selectedOptionId);
    if (selectedOption?.isOther && !(current.otherText || '').trim()) {
      return 'Diğer seçeneği için açıklama zorunludur';
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!survey) return;

    for (const question of survey.questions || []) {
      const errorText = validateQuestion(question);
      if (errorText) {
        setSuccess(false);
        setMessage(errorText);
        return;
      }
    }

    const mappedAnswers = (survey.questions || [])
        .map((question) => {
          const current = answers[question.id] || {};
          if (question.questionType === 'TEXT') {
            const text = (current.answerText || '').trim();
            if (!text) {
              return null;
            }
            return {
              questionId: question.id,
              answerText: text,
            };
          }

          if (!current.selectedOptionId) {
            return null;
          }

          return {
            questionId: question.id,
            selectedOptionId: current.selectedOptionId,
            otherText: (current.otherText || '').trim() || undefined,
          };
        })
        .filter(Boolean) as Array<{ questionId: string; selectedOptionId?: string; answerText?: string; otherText?: string }>;

    const payload = {
      answers: mappedAnswers,
    };

    if (payload.answers.length === 0) {
      setSuccess(false);
      setMessage('En az bir cevap giriniz.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await MailingService.submitPublicSurveyResponse(token, payload);
      setSubmitted(Boolean(response.data.applied));
      setSuccess(Boolean(response.data.applied));
      setMessage(response.data.message || 'İşlem tamamlandı.');
    } catch (error: any) {
      setSuccess(false);
      setMessage(error?.response?.data?.message || 'Anket cevabı kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb', padding: '40px 16px' }}>
      <Row justify='center'>
        <Col xs={24} sm={22} md={18} lg={14} xl={10}>
          <Card>
            <Space direction='vertical' size={16} style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <img src={kontrolxLogo} alt='KONTROLX Logo' style={{ maxHeight: 48 }} />
              </div>
              <Title level={3} style={{ margin: 0 }}>
                KONTROLX Anket
              </Title>

              {loading ? (
                <Space size={12}>
                  <Spin />
                  <Text>Anket yükleniyor...</Text>
                </Space>
              ) : (
                <>
                  {message && (
                    <Alert
                      type={success ? 'success' : 'warning'}
                      showIcon
                      message={success ? 'Bilgilendirme' : 'Uyarı'}
                      description={message}
                    />
                  )}

                  {survey && (
                    <Card size='small'>
                      <Space direction='vertical' size={4}>
                        <Text strong>Kampanya: {survey.campaignName || '-'}</Text>
                        <Text strong>Anket: {survey.surveyName || '-'}</Text>
                        <Text>Yanıt Son Tarihi: {formatDateTime(survey.responseDeadlineAt)}</Text>
                      </Space>
                    </Card>
                  )}

                  {!submitted && survey && (
                    <Space direction='vertical' size={20} style={{ width: '100%' }}>
                      {(survey.questions || []).map((question, index) => {
                        const answer = answers[question.id] || {};
                        const selectedOption = question.options?.find((option) => option.id === answer.selectedOptionId);

                        return (
                          <Card key={question.id} size='small' title={`${index + 1}. ${question.questionText}`}>
                            <Space direction='vertical' size={12} style={{ width: '100%' }}>
                              {question.questionType === 'TEXT' ? (
                                <TextArea
                                  rows={4}
                                  value={answer.answerText}
                                  onChange={(event) =>
                                    setQuestionAnswer(question.id, {
                                      answerText: event.target.value,
                                    })
                                  }
                                  placeholder='Yanıtınızı yazın'
                                  maxLength={1000}
                                />
                              ) : (
                                <>
                                  <Radio.Group
                                    value={answer.selectedOptionId}
                                    onChange={(event) =>
                                      setQuestionAnswer(question.id, {
                                        selectedOptionId: event.target.value,
                                        otherText: undefined,
                                      })
                                    }
                                  >
                                    <Space direction='vertical'>
                                      {(question.options || []).map((option) => (
                                        <Radio key={option.id} value={option.id}>
                                          {option.optionText}
                                        </Radio>
                                      ))}
                                    </Space>
                                  </Radio.Group>
                                  {selectedOption?.isOther && (
                                    <TextArea
                                      rows={3}
                                      value={answer.otherText}
                                      onChange={(event) =>
                                        setQuestionAnswer(question.id, {
                                          otherText: event.target.value,
                                        })
                                      }
                                      placeholder='Diğer seçeneği için açıklama giriniz'
                                      maxLength={500}
                                    />
                                  )}
                                </>
                              )}
                            </Space>
                          </Card>
                        );
                      })}

                      <Button type='primary' onClick={handleSubmit} loading={submitting}>
                        Yanıtları Gönder
                      </Button>
                    </Space>
                  )}
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MailingSurveyPage;
